import { QuestionSet, QuestionSetItem, TeacherQuestion } from '@/types';

/**
 * Validation result for a single question
 */
export interface QuestionValidationResult {
  isValid: boolean;
  errors: string[];
  questionId: string;
  questionIndex: number;
}

/**
 * Validation result for an entire question set
 */
export interface QuestionSetValidationResult {
  isValid: boolean;
  questionResults: QuestionValidationResult[];
  summary: {
    totalQuestions: number;
    validQuestions: number;
    invalidQuestions: number;
    errorCount: number;
  };
}

/**
 * Validates a single teacher question for compulsory fields
 *
 * Compulsory fields:
 * - Question text must not be empty
 * - First two answer choices must not be empty
 */
export const validateQuestion = (
  question: TeacherQuestion,
  questionIndex: number
): QuestionValidationResult => {
  const errors: string[] = [];

  // Check question text
  if (!question.text || question.text.trim() === '') {
    errors.push('Question text is required');
  }

  // Check first answer choice
  if (!question.choices[0] || question.choices[0].trim() === '') {
    errors.push('First answer choice is required');
  }

  // Check second answer choice
  if (!question.choices[1] || question.choices[1].trim() === '') {
    errors.push('Second answer choice is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
    questionId: question.id,
    questionIndex
  };
};

/**
 * Validates all questions in a question set
 * Note: Bonus rounds are not validated (they're optional)
 */
export const validateQuestionSet = (
  questionSet: QuestionSet
): QuestionSetValidationResult => {
  const questionResults: QuestionValidationResult[] = [];
  let questionIndex = 0;

  // Validate each question item (skip bonus rounds)
  for (const item of questionSet.questions) {
    if (item.type === 'question' && item.question) {
      const result = validateQuestion(item.question, questionIndex);
      questionResults.push(result);
      questionIndex++;
    }
  }

  const validQuestions = questionResults.filter(r => r.isValid).length;
  const invalidQuestions = questionResults.filter(r => !r.isValid).length;
  const errorCount = questionResults.reduce((sum, r) => sum + r.errors.length, 0);

  return {
    isValid: invalidQuestions === 0,
    questionResults,
    summary: {
      totalQuestions: questionResults.length,
      validQuestions,
      invalidQuestions,
      errorCount
    }
  };
};

/**
 * Gets a user-friendly error message for validation results
 */
export const getValidationErrorMessage = (
  validationResult: QuestionSetValidationResult
): string => {
  if (validationResult.isValid) {
    return '';
  }

  const { invalidQuestions, errorCount } = validationResult.summary;

  if (invalidQuestions === 1) {
    return `1 question has ${errorCount} missing compulsory field${errorCount > 1 ? 's' : ''}.`;
  }

  return `${invalidQuestions} questions have missing compulsory fields (${errorCount} total).`;
};

/**
 * Gets detailed validation errors for display in UI
 */
export const getDetailedValidationErrors = (
  validationResult: QuestionSetValidationResult
): Array<{ questionIndex: number; questionId: string; errors: string[] }> => {
  return validationResult.questionResults
    .filter(r => !r.isValid)
    .map(r => ({
      questionIndex: r.questionIndex,
      questionId: r.questionId,
      errors: r.errors
    }));
};

/**
 * Checks if a specific question item has validation errors
 */
export const hasValidationErrors = (
  item: QuestionSetItem,
  validationResult: QuestionSetValidationResult | null
): boolean => {
  if (!validationResult || item.type !== 'question' || !item.question) {
    return false;
  }

  const questionResult = validationResult.questionResults.find(
    r => r.questionId === item.question!.id
  );

  return questionResult ? !questionResult.isValid : false;
};

/**
 * Gets validation errors for a specific question
 */
export const getQuestionErrors = (
  item: QuestionSetItem,
  validationResult: QuestionSetValidationResult | null
): string[] => {
  if (!validationResult || item.type !== 'question' || !item.question) {
    return [];
  }

  const questionResult = validationResult.questionResults.find(
    r => r.questionId === item.question!.id
  );

  return questionResult ? questionResult.errors : [];
};
